import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { products } from '../data/products';
import { ProductCard } from '../components/home/ProductCard';
import { ProductPageModal } from '../components/home/ProductPageModal';
import { demoPages } from '../data/demoPages';
import CollectionDialog from '../components/CollectionDialog';
import CollectionShareDialog from '../components/CollectionShareDialog';
import { Button } from '../components/ui/button';
import UserNavMenu from '../components/UserNavMenu';
import axios from 'axios';
import {
  Sparkles, Plus, FolderHeart, Lock, Link2, Globe,
  Pencil, Trash2, Loader2, ArrowRight, ChevronDown, ChevronUp, Share2, Copy
} from 'lucide-react';
import LumeraLogo from '../components/LumeraLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VISIBILITY_ICON = { private: Lock, link: Link2, public: Globe };
const VISIBILITY_LABEL = { private: 'Private', link: 'Shareable', public: 'Public' };

const Collections = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [duplicating, setDuplicating] = useState(null);
  const [sharingCollection, setSharingCollection] = useState(null);

  const fetchCollections = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/collections`);
      setCollections(res.data);
    } catch (err) { console.error('Failed to fetch collections:', err); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authLoading) return; // Wait for auth to complete
    if (user) fetchCollections();
    else navigate('/login');
  }, [user, authLoading, fetchCollections, navigate]);

  const handleSaved = (saved) => {
    setCollections(prev => {
      const idx = prev.findIndex(c => c.id === saved.id);
      if (idx >= 0) { const copy = [...prev]; copy[idx] = saved; return copy; }
      return [saved, ...prev];
    });
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/collections/${id}`);
      setCollections(prev => prev.filter(c => c.id !== id));
    } catch (err) { console.error('Failed to delete collection:', err); }
    setDeleting(null);
  };

  const handleDuplicate = async (id) => {
    setDuplicating(id);
    try {
      const res = await axios.post(`${API}/collections/${id}/duplicate`);
      setCollections(prev => [res.data, ...prev]);
    } catch (err) { console.error('Failed to duplicate collection:', err); }
    setDuplicating(null);
  };

  const handleRemoveProduct = async (collectionId, title) => {
    try {
      await axios.delete(`${API}/collections/${collectionId}/products/${encodeURIComponent(title)}`);
      setCollections(prev => prev.map(c =>
        c.id === collectionId
          ? { ...c, product_titles: c.product_titles.filter(t => t !== title) }
          : c
      ));
    } catch (err) { console.error('Failed to remove product from collection:', err); }
  };

  const getProductsForCollection = (titles) =>
    products.filter(p => titles.includes(p.title));

  if (!user) return null;
  if (authLoading) return (
    <div className="min-h-screen bg-[#F7F5F2] dark:bg-background flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-primary/40 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7F5F2] dark:bg-background" data-testid="collections-page">
      {/* Nav */}
      <nav className="bg-white dark:bg-card border-b border-[#d8cfc8]/50 dark:border-border/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <LumeraLogo size="default" />
          <UserNavMenu size={34} />
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <FolderHeart className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-3xl font-serif font-bold text-stone-900">My Collections</h1>
            </div>
            <p className="text-stone-500 font-sans text-sm">
              Organize products in curated collections and share them with others.
            </p>
          </div>
          <Button
            data-testid="new-collection-btn"
            onClick={() => { setEditingCollection(null); setDialogOpen(true); }}
            className="bg-[#B49A88] hover:bg-[#B49A88] text-white rounded-full px-6 h-10 font-sans font-semibold"
          >
            <Plus className="w-4 h-4 mr-2" /> New collection
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-primary/40 animate-spin" />
          </div>
        ) : collections.length === 0 ? (
          <div className="text-center py-20" data-testid="collections-empty">
            <div className="w-20 h-20 rounded-full bg-stone-50 flex items-center justify-center mx-auto mb-6">
              <FolderHeart className="w-10 h-10 text-stone-300" />
            </div>
            <h3 className="text-xl font-serif font-semibold text-stone-800 mb-2">No collections yet</h3>
            <p className="text-stone-500 font-sans mb-8 max-w-md mx-auto">
              Create your first collection to organize and share your favorite products.
            </p>
            <Button
              data-testid="empty-new-collection-btn"
              onClick={() => { setEditingCollection(null); setDialogOpen(true); }}
              className="bg-[#B49A88] hover:bg-[#B49A88] text-white rounded-full px-8 py-3 font-sans font-semibold shadow-lg"
            >
              Create your first collection <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="collections-list">
            {collections.map((col) => {
              const Icon = VISIBILITY_ICON[col.visibility] || Lock;
              const expanded = expandedId === col.id;
              const colProducts = getProductsForCollection(col.product_titles || []);

              return (
                <div key={col.id} className="bg-white rounded-2xl border border-stone-100 overflow-hidden" data-testid={`collection-${col.id}`}>
                  {/* Collection header */}
                  <div
                    className="flex items-center justify-between p-5 cursor-pointer hover:bg-stone-50/50 transition-colors"
                    onClick={() => setExpandedId(expanded ? null : col.id)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-primary/5 flex items-center justify-center flex-shrink-0">
                        <FolderHeart className="w-5 h-5 text-primary/60" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-sans font-semibold text-stone-800 truncate">{col.name}</h3>
                          <span className="flex items-center gap-1 text-[10px] font-sans font-medium uppercase tracking-wider text-stone-400 flex-shrink-0">
                            <Icon className="w-3 h-3" /> {VISIBILITY_LABEL[col.visibility]}
                          </span>
                        </div>
                        <p className="font-sans text-xs text-stone-400 truncate">
                          {col.product_titles?.length || 0} {(col.product_titles?.length || 0) === 1 ? 'product' : 'products'}
                          {col.description ? ` — ${col.description}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        data-testid={`duplicate-collection-${col.id}`}
                        onClick={(e) => { e.stopPropagation(); handleDuplicate(col.id); }}
                        disabled={duplicating === col.id}
                        className="p-2 text-stone-400 hover:text-emerald-600 rounded-lg transition-colors"
                        title="Dupliser collection"
                      >
                        {duplicating === col.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                      </button>
                      <button
                        data-testid={`share-collection-${col.id}`}
                        onClick={(e) => { e.stopPropagation(); setSharingCollection(col); }}
                        className="p-2 text-stone-400 hover:text-[#B49A88] rounded-lg transition-colors"
                        title="Share collection"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`edit-collection-${col.id}`}
                        onClick={(e) => { e.stopPropagation(); setEditingCollection(col); setDialogOpen(true); }}
                        className="p-2 text-stone-400 hover:text-primary rounded-lg transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-collection-${col.id}`}
                        onClick={(e) => { e.stopPropagation(); handleDelete(col.id); }}
                        disabled={deleting === col.id}
                        className="p-2 text-stone-400 hover:text-red-500 rounded-lg transition-colors"
                      >
                        {deleting === col.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </button>
                      {expanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
                    </div>
                  </div>

                  {/* Expanded: show products */}
                  {expanded && (
                    <div className="border-t border-stone-100 p-5">
                      {colProducts.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="font-sans text-sm text-stone-400 mb-3">No products in this collection yet.</p>
                          <Button
                            onClick={() => navigate('/')}
                            variant="outline"
                            className="rounded-full px-5 h-8 text-xs font-sans font-semibold border-stone-200"
                          >
                            Add fra markedsplassen
                          </Button>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {colProducts.map(product => (
                            <div key={product.title} className="relative group">
                              <ProductCard
                                {...product}
                                onClick={() => setSelectedProduct(product)}
                              />
                              <button
                                data-testid={`remove-from-collection-${product.title.toLowerCase().replace(/\s+/g, '-')}`}
                                onClick={() => handleRemoveProduct(col.id, product.title)}
                                className="absolute top-4 left-4 z-30 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-red-600"
                                title="Remove from collection"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <CollectionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        collection={editingCollection}
        onSaved={handleSaved}
      />

      <CollectionShareDialog
        open={!!sharingCollection}
        onOpenChange={(open) => { if (!open) setSharingCollection(null); }}
        collection={sharingCollection}
        onCollectionUpdated={(updated) => {
          setCollections(prev => prev.map(c => c.id === updated.id ? updated : c));
          setSharingCollection(updated);
        }}
      />

      {selectedProduct && demoPages[selectedProduct.title] && (
        <ProductPageModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onCreateProduct={() => navigate(`/generator?template=${encodeURIComponent(selectedProduct.title)}`)}
          onNextProduct={() => {}}
          allProducts={products}
        />
      )}
    </div>
  );
};

export default Collections;
